import type { FireContext } from './types';
import { isOpenAICommerceEvent, mapPlatformEvent } from './eventMaps';

const SDK = 'https://bzrcdn.openai.com/sdk/oaiq.min.js';

function ensureOaiq(): void {
  const w = window as any;
  if (!w.oaiq) {
    const q: any = (...args: any[]) => q.q.push(args);
    q.q = [];
    w.oaiq = q;
  }
  if (!document.querySelector(`script[src="${SDK}"]`)) {
    const script = document.createElement('script');
    script.async = true;
    script.src = SDK;
    document.head.appendChild(script);
  }
}

// Bentuk data ikuti contoh docs measurement-pixel persis:
// - content view: TANPA amount/currency/item-detail (amount 0 + currency = invalid).
// - commerce: amount+currency HANYA bila amount > 0 (top-level + item-level).
export function buildOpenAIData(ctx: Pick<FireContext, 'contentName' | 'contentIds' | 'orderData' | 'value'>, event: string) {
  if (event === 'lead_created') return { type: 'customer_action' };
  const amount = Math.round(Number(ctx.orderData?.value ?? ctx.orderData?.amount ?? ctx.value ?? 0));
  const ids = ctx.orderData?.contentIds || ctx.contentIds || [];
  const currency = ctx.orderData?.currency || 'IDR';
  const hasAmount = Number.isFinite(amount) && amount > 0;
  if (!isOpenAICommerceEvent(event)) {
    const base = { type: 'contents' } as Record<string, unknown>;
    if (!ids.length) return base;
    return { ...base, contents: ids.map(id => ({ id, name: ctx.contentName || id, content_type: 'product' })) };
  }
  const base = { type: 'contents' } as Record<string, unknown>;
  if (hasAmount) { base.currency = currency; base.amount = amount; }
  if (!ids.length) return base;
  return {
    ...base,
    contents: ids.map(id => {
      const item = { id, name: ctx.contentName || id, content_type: 'product', quantity: ctx.orderData?.quantity || 1 } as Record<string, unknown>;
      if (hasAmount) { item.amount = amount; item.currency = currency; }
      return item;
    }),
  };
}

async function sha256Hex(raw: string): Promise<string | null> {
  try {
    const data = new TextEncoder().encode(raw);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return [...new Uint8Array(digest)].map(b => b.toString(16).padStart(2, '0')).join('');
  } catch { return null; }
}

function normalizeEmail(v?: string): string | null {
  const s = (v || '').trim().toLowerCase();
  return s || null;
}

function normalizePhone(v?: string): string | null {
  const d = (v || '').replace(/[^\d]/g, '').replace(/^0+/, '');
  return d.length >= 8 && d.length <= 15 ? d : null;
}

// Docs: lowercase + buang whitespace & ASCII punctuation, non-ASCII dipertahankan.
function normalizeName(v?: string): string | null {
  const s = (v || '').toLowerCase().replace(/[\s\x21-\x2F\x3A-\x40\x5B-\x60\x7B-\x7E]/g, '');
  return s || null;
}

async function fireUserInit(pixelId: string, user?: FireContext['userData']): Promise<void> {
  if (!user) return;
  const email = normalizeEmail(user.email);
  const phone = normalizePhone(user.phone);
  const tokens = (user.name || '').split(/\s+/).map(t => normalizeName(t)).filter((t): t is string => !!t);
  const userObj: Record<string, unknown> = {};
  if (email) { const h = await sha256Hex(email); if (h) userObj.email_sha256 = h; }
  if (phone) { const h = await sha256Hex(phone); if (h) userObj.phone_number_sha256 = h; }
  if (user.externalId) { const h = await sha256Hex(user.externalId.trim()); if (h) userObj.external_id_sha256 = h; }
  if (tokens.length) { const h = await sha256Hex(tokens[0]); if (h) userObj.first_name_sha256 = h; }
  if (tokens.length > 1) { const h = await sha256Hex(tokens[tokens.length - 1]); if (h && h !== userObj.first_name_sha256) userObj.last_name_sha256 = h; }
  if (user.country) userObj.country = user.country;
  if (user.city) userObj.city = user.city;
  if (user.province) userObj.region = user.province;
  if (user.postalCode) userObj.postal_code = user.postalCode;
  if (!Object.keys(userObj).length) return;
  // Multi-pixel: wajib sertakan pixelId target (docs multiple-pixels).
  try { (window as any).oaiq('init', { pixelId, user: userObj }); } catch { /* non-blocking */ }
}

export function fireOpenAIPixels(ctx: FireContext): void {
  const configs = ctx.configs.filter(c => c.platform === 'openai');
  if (!configs.length) return;
  ensureOaiq();
  configs.forEach(config => {
    const event = mapPlatformEvent('openai', config.eventName);
    config.pixelIds.filter(Boolean).forEach(pixelId => {
      const eventId = ctx.orderData?.orderId || ctx.eventId;
      try {
        (window as any).oaiq('init', { pixelId });
        (window as any).oaiq('measureSingle', pixelId, event, buildOpenAIData(ctx, event), eventId ? { event_id: eventId } : {});
        void fireUserInit(pixelId, ctx.userData);
      } catch { /* tracking tidak boleh blokir checkout */ }
    });
  });
}
